import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Loader2, Megaphone, DollarSign, Eye, EyeOff } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PromoterSignupPage() {
  const [searchParams] = useSearchParams();
  const inviteId = searchParams.get('invite');
  const navigate = useNavigate();
  const { user, register } = useAuth();

  const [invite, setInvite] = useState(null);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!inviteId) return;
    axios.get(`${API}/promo/invite/${inviteId}`)
      .then(r => { setInvite(r.data); if (r.data.email) setEmail(r.data.email); })
      .catch(() => {});
  }, [inviteId]);

  // Already logged in → just enroll + accept and go to the dashboard.
  useEffect(() => {
    if (!user) return;
    (async () => {
      const headers = { Authorization: `Bearer ${localStorage.getItem('eventism_token')}` };
      try { await axios.post(`${API}/street-team/opt-in`, {}, { headers }); } catch { /* may be banned */ }
      if (invite?.event_id) {
        try { await axios.post(`${API}/promo/events/${invite.event_id}/accept`, {}, { headers }); } catch { /* already accepted */ }
      }
      navigate('/dashboard/street-team');
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, invite?.event_id]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name || !email || !password) return toast.error('Please fill in all fields');
    if (password.length < 6) return toast.error('Password must be at least 6 characters');
    setLoading(true);
    try {
      await register(email, password, name, 'client');
      const headers = { Authorization: `Bearer ${localStorage.getItem('eventism_token')}` };
      await axios.post(`${API}/street-team/opt-in`, {}, { headers });
      if (invite?.event_id) {
        try { await axios.post(`${API}/promo/events/${invite.event_id}/accept`, {}, { headers }); } catch { /* non-fatal */ }
      }
      toast.success(invite ? `You're on the promo team for ${invite.event_name}!` : 'Welcome to the street team!');
      navigate('/dashboard/street-team');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Signup failed');
    } finally {
      setLoading(false);
    }
  };

  const loginUrl = invite?.event_id
    ? `/login?returnTo=${encodeURIComponent(`/promo-partners?intent=accept&event_id=${invite.event_id}`)}`
    : `/login?returnTo=${encodeURIComponent('/promoter-signup' + (inviteId ? `?invite=${inviteId}` : ''))}`;

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-emerald-500/10 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-indigo-500/15 rounded-full blur-3xl" />
      </div>

      <Card className="w-full max-w-md relative z-10 bg-card/90 backdrop-blur-xl border-border" data-testid="promoter-signup-card">
        <CardHeader className="text-center">
          <Link to="/" className="inline-flex items-center justify-center space-x-2 mb-4">
            <img src="/eventism-logo.png" alt="Eventism" className="h-7 sm:h-8" style={{ filter: 'brightness(0) invert(1)' }} />
          </Link>
          <div className="mx-auto h-11 w-11 rounded-full bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center mb-2">
            <Megaphone className="h-5 w-5 text-emerald-400" />
          </div>
          <CardTitle className="text-2xl font-heading text-foreground">
            {invite ? `Promote ${invite.event_name}` : 'Become an Eventism promoter'}
          </CardTitle>
          <CardDescription className="text-muted-foreground">
            {invite
              ? <>{invite.org_name ? `${invite.org_name} invited you. ` : ''}Create your promoter account — takes 30 seconds, no other choices needed.</>
              : 'Share events with your tracked link and earn a commission on every ticket.'}
          </CardDescription>
          {invite?.commission_label && (
            <div className="mt-2 inline-flex items-center gap-1.5 mx-auto text-xs font-semibold text-emerald-400 bg-emerald-500/10 border border-emerald-500/30 rounded-full px-3 py-1" data-testid="invite-commission">
              <DollarSign className="h-3.5 w-3.5" /> Earn {invite.commission_label}
            </div>
          )}
        </CardHeader>
        <CardContent className="space-y-4">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-1.5">
              <Label className="text-muted-foreground">Your name</Label>
              <Input value={name} onChange={e => setName(e.target.value)} placeholder="Alex Rivera" data-testid="promoter-name" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-muted-foreground">Email</Label>
              <Input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="you@example.com" data-testid="promoter-email" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-muted-foreground">Password</Label>
              <div className="relative">
                <Input type={showPassword ? 'text' : 'password'} value={password} onChange={e => setPassword(e.target.value)} placeholder="6+ characters" data-testid="promoter-password" />
                <button type="button" onClick={() => setShowPassword(s => !s)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground">
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
            <Button type="submit" disabled={loading} className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-semibold" data-testid="promoter-signup-submit">
              {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              {invite ? 'Join the promo team →' : 'Create promoter account →'}
            </Button>
          </form>
          <p className="text-xs text-center text-muted-foreground">
            Already have an Eventism account?{' '}
            <Link to={loginUrl} className="text-indigo-400 hover:underline font-medium" data-testid="promoter-login-link">Log in</Link>
            {invite ? " — we'll add you to the promo team automatically." : ''}
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
